"use client";

import { cn } from "@/lib/utils";
import Autoplay from "embla-carousel-autoplay";
import type { EmblaPluginType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  COVERFLOW_CARD_GAP_PX,
  COVERFLOW_CARD_W_PX,
  COVERFLOW_STRIDE_PX,
  COVERFLOW_TILT_PERSPECTIVE_PX,
  computeCoverflowVisuals,
  pickClosestSlideIndices,
} from "./coverflowCarouselUtils";

export type CarouselItem = {
  id: string;
  image: string;
  name: string;
  designation: string;
  company: string;
  logo: string;
  /** Optional line under designation */
  subtitle?: string;
  /** Matches Visionaries profile_name_color */
  nameColor?: string;
  /** Matches Visionaries profile_title_color */
  designationColor?: string;
  logo2?: string;
  logo2Alt?: string;
  logoAlt?: string;
  /** `stack` | `row` — same as Visionaries logos_layout */
  logosLayout?: "stack" | "row";
};

export type CoverflowCarouselProps = {
  items: CarouselItem[];
  autoplay?: boolean;
  delay?: number;
  className?: string;
  loop?: boolean;
  /**
   * When true, stretches to viewport width with `pl-[25px]` and no right inset
   * so the track can bleed past the parent (Visionaries-style).
   */
  fullBleedRight?: boolean;
  /**
   * Blurred portrait wash behind the row (active slide), plus base `#030306` and edge vignette.
   * Default **true**; set `false` if the parent background should show through instead.
   */
  ambientBackground?: boolean;
};

const MOBILE_BREAKPOINT = 768;
const CARD_W = COVERFLOW_CARD_W_PX;
const CARD_GAP_PX = COVERFLOW_CARD_GAP_PX;

type EmblaSlideRow = { item: CarouselItem; reactKey: string };

/**
 * Embla only keeps `loop: true` active when total scroll width is well above the viewport.
 * Without enough slides, it silently falls back to a finite strip — duplicate items until the math works.
 */
function buildEmblaSlideRows(
  items: CarouselItem[],
  enableLoop: boolean,
  viewportWidth: number
): EmblaSlideRow[] {
  if (items.length === 0) return [];
  const base = items.map((item, i) => ({
    item,
    reactKey: `coverflow-${item.id}-b${i}`,
  }));
  if (!enableLoop) return base;

  const vw = Math.max(viewportWidth, 320);
  /** Generous runway so Embla never falls back to finite scroll (huge void after last → first). */
  const minScrollWidth = Math.max(vw * 6, 4800);
  const minSlidesNeeded = Math.max(
    36,
    Math.ceil(minScrollWidth / COVERFLOW_STRIDE_PX) + 12,
    items.length * 10
  );
  let rows = [...base];
  let dup = 0;
  while (
    (rows.length < minSlidesNeeded || rows.length * COVERFLOW_STRIDE_PX < minScrollWidth) &&
    rows.length < 280
  ) {
    rows = rows.concat(
      items.map((item, i) => ({
        item,
        reactKey: `coverflow-${item.id}-d${dup}-${i}`,
      }))
    );
    dup++;
  }
  return rows;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

function useViewportIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

/** Filter / opacity on the slide wrapper; transform only on the card (same-layer blur kills 3D). */
const COVERFLOW_SHELL_TRANSITION = "filter 120ms ease-out, opacity 120ms ease-out";

function applySlideVisuals(
  slide: HTMLElement,
  inner: HTMLElement,
  v: ReturnType<typeof computeCoverflowVisuals>,
  prefersReducedMotion: boolean
) {
  slide.style.transition = prefersReducedMotion ? "none" : COVERFLOW_SHELL_TRANSITION;
  slide.style.opacity = String(v.shellOpacity);
  slide.style.filter = v.shellFilter;
  slide.style.zIndex = String(v.zIndex);

  inner.style.transition = "none";
  inner.style.transformOrigin = v.transformOrigin;
  inner.style.transform = v.cardTransform;
  inner.style.boxShadow = v.boxShadow;
  inner.style.filter = "none";
  inner.style.opacity = "1";

  const media = inner.querySelector("[data-coverflow-media]");
  if (media instanceof HTMLElement) {
    media.style.opacity = "1";
    media.style.filter = "none";
    media.style.transition = "none";
  }
}

const EMBLA_NO_PLUGINS: EmblaPluginType[] = [];

export function CoverflowCarousel({
  items,
  autoplay = false,
  delay = 3000,
  className,
  loop = true,
  fullBleedRight = false,
  ambientBackground = true,
}: CoverflowCarouselProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobileViewport = useViewportIsMobile();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const trackPadSig = useRef<string>("");
  const resizePadDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafId = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  /** Opacity of the blurred portrait wash (canvas sampling fails on most CMS URLs without CORS; blur `<img>` does not). */
  const [ambientReveal, setAmbientReveal] = useState(1);
  const ambientSkipNextFade = useRef(true);
  const wheelAccum = useRef(0);
  const wheelCooldown = useRef(false);
  const wheelCooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const plugins = useMemo(() => {
    if (!autoplay) return EMBLA_NO_PLUGINS;
    return [
      Autoplay({
        delay,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      }),
    ];
  }, [autoplay, delay]);

  const [viewportRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      /** Native center alignment — `start` + huge symmetric padding fought Embla’s snaps and read as a hollow middle band. */
      align: "center",
      axis: "x",
      dragFree: false,
      skipSnaps: false,
      containScroll: loop ? false : "trimSnaps",
      duration: prefersReducedMotion ? 0 : 68,
      dragThreshold: 14,
      /** Pick up duplicated loop slides when slide count changes. */
      watchSlides: true,
      watchResize: true,
    },
    plugins
  );

  const mode = isMobileViewport ? "mobile" : "desktop";

  const flushTransforms = useCallback(() => {
    const api = emblaApi;
    if (!api) return;
    const viewport = api.rootNode();
    const slides = api.slideNodes();
    if (slides.length === 0) return;
    const rect = viewport.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    const centers = slides.map((slide) => {
      const sr = slide.getBoundingClientRect();
      return sr.left + sr.width / 2;
    });

    /** Framer-style: the three slides whose centers are nearest the viewport stay fully sharp (no blur / full opacity). */
    const sharpIndices = pickClosestSlideIndices(centers, centerX, 3);

    slides.forEach((slide, i) => {
      /** Embla loop reorders slide nodes in the DOM — never index a parallel ref array by `i`. */
      const inner =
        slide.querySelector<HTMLElement>("[data-coverflow-card-inner]") ??
        (slide.firstElementChild instanceof HTMLElement
          ? slide.firstElementChild
          : null);
      if (!inner) return;
      const slideCenter = centers[i]!;
      const offsetSlides =
        (slideCenter - centerX) / Math.max(COVERFLOW_STRIDE_PX, 1);

      let visuals = computeCoverflowVisuals(offsetSlides, mode);
      if (prefersReducedMotion) {
        const depth = Math.min(Math.abs(offsetSlides * 0.35), 2);
        visuals = {
          ...visuals,
          cardTransform: `perspective(${COVERFLOW_TILT_PERSPECTIVE_PX}px) rotateX(0deg) rotateY(0deg) scale(${visuals.cardScale.toFixed(4)})`,
          shellFilter: "none",
          shellOpacity: Math.max(0.58, 1 - depth * 0.12),
        };
      } else if (sharpIndices.has(i)) {
        visuals = {
          ...visuals,
          /** `blur(0)` still creates a filter layer in some engines — `none` keeps 3D crisp on neighbours. */
          shellFilter: "none",
          shellOpacity: 1,
        };
      }
      applySlideVisuals(slide, inner, visuals, prefersReducedMotion);
    });
  }, [emblaApi, mode, prefersReducedMotion]);

  const scheduleFlush = useCallback(() => {
    if (rafId.current != null) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      flushTransforms();
    });
  }, [flushTransforms]);

  const itemsKey = useMemo(() => items.map((it) => it.id).join("|"), [items]);

  const activePortraitUrl = useMemo(() => {
    if (!items.length) return "";
    const idx = Math.min(Math.max(0, activeIndex), items.length - 1);
    return items[idx]?.image?.trim() ?? "";
  }, [items, activeIndex]);

  useEffect(() => {
    if (!ambientBackground) return;
    if (!activePortraitUrl) {
      setAmbientReveal(1);
      return;
    }
    if (!prefersReducedMotion && !ambientSkipNextFade.current) {
      setAmbientReveal(0.22);
    }
    ambientSkipNextFade.current = false;
  }, [activePortraitUrl, ambientBackground, prefersReducedMotion]);

  useEffect(() => {
    ambientSkipNextFade.current = true;
  }, [itemsKey]);

  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1440)
  );

  const displaySlides = useMemo(
    () => buildEmblaSlideRows(items, loop, viewportWidth),
    [items, loop, viewportWidth]
  );

  useLayoutEffect(() => {
    scheduleFlush();
  }, [items, displaySlides.length, scheduleFlush]);

  const applyTrackScrollPadding = useCallback(() => {
    const api = emblaApi;
    const track = trackRef.current;
    if (!api || !track) return;
    const vp = api.rootNode();
    const w = vp.clientWidth;
    setViewportWidth((prev) => (w !== prev ? w : prev));

    /** Wait for React to commit `displaySlides` for this width before reInit — avoids intermittent loop loss. */
    if (loop) {
      const neededLen = buildEmblaSlideRows(items, true, w).length;
      if (neededLen !== displaySlides.length) {
        trackPadSig.current = "";
        scheduleFlush();
        return;
      }
    }

    /** Small gutters only — centering comes from `align: "center"` (large padding here caused empty middle + snap mismatch). */
    const gutter = fullBleedRight ? 25 : 16;
    const pl = `${gutter}px`;
    const pr = `${gutter}px`;
    const sig = `${pl}|${pr}|${w}|${itemsKey}|${displaySlides.length}`;
    if (trackPadSig.current === sig) {
      scheduleFlush();
      return;
    }
    trackPadSig.current = sig;
    track.style.paddingLeft = pl;
    track.style.paddingRight = pr;
    api.reInit();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const snaps = api.scrollSnapList();
        const maxIdx = Math.max(0, snaps.length - 1);
        const i = Math.min(api.selectedScrollSnap(), maxIdx);
        api.scrollTo(i, true);
        scheduleFlush();
      });
    });
  }, [
    emblaApi,
    fullBleedRight,
    loop,
    items,
    displaySlides.length,
    itemsKey,
    scheduleFlush,
  ]);

  useLayoutEffect(() => {
    applyTrackScrollPadding();
  }, [applyTrackScrollPadding, itemsKey, displaySlides.length]);

  useEffect(() => {
    const api = emblaApi;
    if (!api) return;

    const logicalLen = Math.max(items.length, 1);
    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap() % logicalLen);
    };

    const onSettle = () => {
      scheduleFlush();
    };

    onSelect();
    api.on("select", onSelect);
    api.on("scroll", scheduleFlush);
    api.on("slideFocus", scheduleFlush);
    api.on("resize", scheduleFlush);
    api.on("reInit", scheduleFlush);
    api.on("settle", onSettle);

    scheduleFlush();

    const ro = new ResizeObserver(() => {
      if (resizePadDebounce.current) clearTimeout(resizePadDebounce.current);
      resizePadDebounce.current = setTimeout(() => {
        resizePadDebounce.current = null;
        applyTrackScrollPadding();
      }, 48);
    });
    ro.observe(api.rootNode());

    return () => {
      api.off("select", onSelect);
      api.off("scroll", scheduleFlush);
      api.off("slideFocus", scheduleFlush);
      api.off("resize", scheduleFlush);
      api.off("reInit", scheduleFlush);
      api.off("settle", onSettle);
      ro.disconnect();
      if (resizePadDebounce.current) clearTimeout(resizePadDebounce.current);
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      if (wheelCooldownTimer.current) clearTimeout(wheelCooldownTimer.current);
    };
  }, [emblaApi, items.length, scheduleFlush, applyTrackScrollPadding]);

  const releaseWheelCooldown = useCallback(() => {
    wheelCooldown.current = false;
    wheelCooldownTimer.current = null;
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!emblaApi) return;
      const { deltaX, deltaY, shiftKey } = e;
      const ax = Math.abs(deltaX);
      const ay = Math.abs(deltaY);
      const horizontalDominant = shiftKey || ax > ay;

      const delta = horizontalDominant ? deltaX : deltaY;
      if (delta === 0) return;

      if (horizontalDominant && e.cancelable) e.preventDefault();

      wheelAccum.current += delta;

      const threshold = horizontalDominant ? 38 : 70;
      if (Math.abs(wheelAccum.current) < threshold) {
        return;
      }

      if (wheelCooldown.current) {
        if (e.cancelable) e.preventDefault();
        return;
      }

      if (e.cancelable) e.preventDefault();
      const sum = wheelAccum.current;
      wheelAccum.current = 0;
      wheelCooldown.current = true;
      if (wheelCooldownTimer.current) clearTimeout(wheelCooldownTimer.current);
      wheelCooldownTimer.current = setTimeout(releaseWheelCooldown, 320);

      if (horizontalDominant) {
        /** Match trackpads: scroll right / finger right → advance (next slide). */
        if (sum > 0) emblaApi.scrollNext();
        else emblaApi.scrollPrev();
      } else {
        /** Positive deltaY (scroll down) → next. */
        if (sum > 0) emblaApi.scrollNext();
        else emblaApi.scrollPrev();
      }
    },
    [emblaApi, releaseWheelCooldown]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!emblaApi) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        emblaApi.scrollPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        emblaApi.scrollNext();
      } else if (e.key === "Home") {
        e.preventDefault();
        emblaApi.scrollTo(0);
      } else if (e.key === "End") {
        e.preventDefault();
        emblaApi.scrollTo(Math.max(0, displaySlides.length - 1));
      }
    },
    [emblaApi, displaySlides.length]
  );

  if (!items.length) {
    return null;
  }

  const rootBleed = fullBleedRight
    ? "relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 bg-transparent pl-[25px] px-0"
    : "relative w-full bg-transparent px-0";

  return (
    <section
      className={cn(rootBleed, "select-none text-white", className)}
      aria-roledescription="carousel"
      aria-label="Featured profiles carousel"
    >
      {ambientBackground ? (
        <div
          className="pointer-events-none absolute inset-0 z-[1] overflow-hidden"
          aria-hidden
        >
          <div className="absolute inset-0 bg-[#030306]" />
          {activePortraitUrl ? (
            <div
              className={cn(
                "absolute inset-0",
                prefersReducedMotion ? "" : "transition-opacity duration-500 ease-out"
              )}
              style={{ opacity: ambientReveal }}
            >
              <img
                key={activePortraitUrl}
                src={activePortraitUrl}
                alt=""
                decoding="async"
                onLoad={() => {
                  if (prefersReducedMotion) {
                    setAmbientReveal(1);
                    return;
                  }
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => setAmbientReveal(1));
                  });
                }}
                onError={() => setAmbientReveal(1)}
                className="pointer-events-none absolute left-1/2 top-1/2 max-w-none object-cover saturate-[1.18]"
                style={{
                  width: "min(260vw, 2800px)",
                  height: "min(160vh, 1400px)",
                  opacity: 0.56,
                  filter: isMobileViewport ? "blur(24px)" : "blur(32px)",
                  transform: "translate(-50%, -50%) scale(1.55)",
                  willChange: "filter, opacity",
                }}
              />
            </div>
          ) : null}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.78) 0%, transparent 10%, transparent 90%, rgba(0,0,0,0.78) 100%)",
            }}
          />
        </div>
      ) : null}
      {/* Soft edge fades only — no solid “cardboard” frame */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-16 bg-gradient-to-r from-black/35 to-transparent sm:w-24"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-20 bg-gradient-to-l from-black/25 to-transparent"
        aria-hidden
      />

      <div className="relative z-[3] py-[50px]">
        <div
          ref={viewportRef}
          className={cn(
            /** `overflow-hidden` clips 3D corners — inset with padding so tilted cards clear the mask. */
            "overflow-hidden px-4 py-7 outline-none sm:px-5 sm:py-8",
            "focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-0",
            "cursor-grab touch-pan-x active:cursor-grabbing"
          )}
          tabIndex={0}
          role="region"
          aria-label="Swipe or use arrow keys to explore slides"
          aria-live="polite"
          onWheel={onWheel}
          onKeyDown={onKeyDown}
        >
          {/** Use margin between slides, not flex `gap` — Embla measures slide nodes for snaps; gap often mis-measures and causes end voids. */}
          <div
            ref={trackRef}
            className="flex"
            style={{ transformStyle: "preserve-3d" }}
          >
            {displaySlides.map((row, index) => {
              const item = row.item;
              const logosRow = item.logosLayout === "stack";
              const hasLogo1 = item.logo && item.logo.trim() !== "";
              const hasLogo2 = item.logo2 && item.logo2.trim() !== "";
              const showCompany = item.company && item.company.trim() !== "";
              const logicalLen = Math.max(items.length, 1);
              const isActiveLogical =
                loop && items.length > 0
                  ? index % logicalLen === activeIndex
                  : index === activeIndex;

              return (
                <div
                  key={row.reactKey}
                  className="relative flex min-w-0 shrink-0 grow-0 items-center justify-center will-change-[filter,opacity]"
                  style={{
                    flex: `0 0 ${CARD_W}px`,
                    width: CARD_W,
                    minWidth: CARD_W,
                    marginRight: CARD_GAP_PX,
                  }}
                >
                  <div
                    data-coverflow-card-inner
                    className={cn(
                      "relative transform-gpu rounded-[20px] will-change-transform",
                      "max-[1079px]:aspect-[335/410] max-[1079px]:h-auto max-[1079px]:w-[min(100%,310px)]",
                      "min-[1080px]:h-[450px] min-[1080px]:w-[310px] min-[1080px]:max-w-[310px]"
                    )}
                    style={{
                      transformStyle: "preserve-3d",
                    }}
                    aria-current={isActiveLogical ? "true" : undefined}
                  >
                    {/** Clip + radius live here — not on the 3D node (avoids the “bent frame” look). */}
                    <div className="absolute inset-0 overflow-hidden rounded-[20px] bg-neutral-900">
                    <div
                      data-coverflow-media
                      className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[20px]"
                    >
                      <div className="absolute inset-0 overflow-hidden rounded-[20px]">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-full w-full object-cover object-center"
                          draggable={false}
                          loading={index === 0 ? "eager" : "lazy"}
                        />
                      </div>

                      <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] hidden h-[min(210px,52%)] min-h-[140px] min-[1080px]:block"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.97), rgba(0,0,0,0.75) 45%, rgba(0,0,0,0.2) 72%, transparent 88%)",
                        }}
                        aria-hidden
                      />
                      <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] block min-[1080px]:hidden h-[min(163px,42%)] min-h-[120px]"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.88), rgba(0,0,0,0.4) 45%, transparent 78%)",
                        }}
                        aria-hidden
                      />
                    </div>

                    {/* Visionaries ProfileCard-aligned footer */}
                    <div
                      className={cn(
                        "pointer-events-none absolute bottom-0 left-1/2 z-10 flex w-full max-w-[min(100%,310px)] -translate-x-1/2 flex-col items-center gap-2 px-3 pb-6 pt-0",
                        "max-[1079px]:max-w-full max-[1079px]:gap-2.5 max-[1079px]:px-5 max-[1079px]:pb-6 max-[1079px]:pt-6",
                        "min-[1080px]:w-[310px] min-[1080px]:max-w-[310px] min-[1080px]:pb-6",
                        "min-[1080px]:gap-2 min-[1230px]:gap-2 min-[1230px]:pb-5",
                        "min-[1430px]:gap-2.5 min-[1430px]:pb-[34px]"
                      )}
                    >
                      <div className="flex w-full max-w-full flex-col items-center gap-2 min-[1430px]:gap-2.5 max-[1079px]:gap-2.5">
                        <p
                          className={cn(
                            "w-full min-w-0 text-center text-sm font-semibold leading-[1.4] tracking-[0.14px] text-white",
                            "max-[1079px]:text-2xl max-[1079px]:leading-[35px] max-[1079px]:tracking-[0.24px] max-[1079px]:truncate",
                            "min-[1080px]:text-base min-[1080px]:tracking-[0.16px]",
                            "min-[1230px]:text-[22px] min-[1230px]:tracking-[0.22px]",
                            "min-[1430px]:text-[26px] min-[1750px]:tracking-[0.26px]",
                            "min-[1430px]:truncate"
                          )}
                          style={{
                            color: item.nameColor || "#FFFFFF",
                            fontFamily: "var(--font-wf-visual-sans)",
                          }}
                        >
                          {item.name}
                        </p>
                        <div
                          className="h-px w-[30.625px] shrink-0 rounded-full bg-white/80 max-[1079px]:w-[35px] min-[1430px]:w-[35px]"
                          aria-hidden
                        />
                        <div className="flex w-full flex-col items-center gap-1 min-[1430px]:gap-[5px]">
                          <p
                            className={cn(
                              "w-full min-w-0 text-center text-[10px] font-medium italic leading-[1.4] tracking-[0.1px] text-white/80",
                              "max-[1079px]:text-xs max-[1079px]:tracking-[0.12px]",
                              "min-[1430px]:text-xs min-[1750px]:tracking-[0.12px]"
                            )}
                            style={{
                              color:
                                item.designationColor || "rgba(255,255,255,0.8)",
                              fontFamily: "var(--font-wf-visual-sans)",
                            }}
                          >
                            {item.designation}
                          </p>
                          {item.subtitle ? (
                            <p
                              className="w-full text-center text-[10px] font-medium uppercase tracking-[0.14em] text-white/55 max-[1079px]:text-[11px]"
                              style={{ fontFamily: "var(--font-wf-visual-sans)" }}
                            >
                              {item.subtitle}
                            </p>
                          ) : null}
                          <div
                            className={cn(
                              "mt-1 flex w-full flex-wrap items-center justify-center gap-2",
                              logosRow ? "flex-col" : "flex-row"
                            )}
                          >
                            {hasLogo1 ? (
                              <div className="relative h-[25px] w-[88px] max-[1079px]:h-[34px] max-[1079px]:w-full max-[1079px]:max-w-[121px] min-[1230px]:h-[28px] min-[1230px]:w-[100px] min-[1430px]:aspect-[940/268] min-[1430px]:h-auto min-[1430px]:max-h-[34px] min-[1430px]:w-full">
                                <img
                                  src={item.logo}
                                  alt={item.logoAlt || item.company || ""}
                                  className="h-full w-full object-contain"
                                  draggable={false}
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                            {hasLogo2 ? (
                              <div className="relative h-[25px] w-[88px] max-[1079px]:h-[34px] max-[1079px]:w-full max-[1079px]:max-w-[121px] min-[1230px]:h-[28px] min-[1230px]:w-[100px] min-[1430px]:aspect-[940/268] min-[1430px]:h-auto min-[1430px]:max-h-[34px] min-[1430px]:w-full">
                                <img
                                  src={item.logo2}
                                  alt={item.logo2Alt || ""}
                                  className="h-full w-full object-contain"
                                  draggable={false}
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                            {showCompany ? (
                              <span
                                className="text-center text-xs font-bold uppercase tracking-wide text-white max-[1079px]:text-sm min-[1430px]:text-sm"
                                style={{ fontFamily: "var(--font-wf-visual-sans)" }}
                              >
                                {item.company}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CoverflowCarousel;
